/**
 * Encrypted orgs: an org is one ledger, of one kind. A plain org's ledger is on
 * the server (cloud mode's socket); an encrypted org's (a *vault*, tenantdb/sync
 * and docs/sync-plan.md) is on every device of its people, in a file of its own
 * — `vaultFile(org)` — and travels between them end-to-end encrypted. The
 * server keeps sealed commits in one line and takes a push only on its head; a
 * device that is behind merges its own changes over what it pulled and pushes
 * again. It never sees an amount, a name or a date.
 *
 * Both kinds are cloud mode (`okane.mode`, `okane.org`); the org's kind decides
 * which. What is kept where:
 *   localStorage `okane.vault`  `{ id, name }` — the encrypted org this device
 *                               has open, so it opens offline too (main.js)
 *   IndexedDB `okane-sync`      the device: its id and its key pair, the private
 *                               key unreadable even to this code
 *   the org's file              the ledger, and `_sync_*`: what is waiting to be
 *                               pushed, where in the history it is, conflicts
 *
 * Privacy and language are this device's own (settings rows that do not sync).
 */
import { createVault, getMeta, newDevice, uninstall } from 'tenantdb/sync'
import { activeDriver, afterWrite, direct, exportDatabase, hold, importDatabase, migrateNow, openLocalDriver, vaultFile } from '../db'
import { copyLedger } from '../db/move'
import { dropLater } from '../db/drops'
import { MIGRATIONS } from '../db/schema'
import * as cloud from './cloud'
import { state } from './state'
import { hydrate } from './index'

export { vaultFile }

const VAULT_KEY = 'okane.vault'
const LOCAL = { settings: (ref) => `${ref}.key IN ('privacy', 'language')` }

const kept = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null')
  } catch {
    return null
  }
}
const keep = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private windows: it lasts as long as the page.
  }
}

/** The encrypted org this device has open, or null. */
export const vaultOrg = () => kept(VAULT_KEY)
/** Remembers (or forgets) the encrypted org this device has open. */
export const setVaultOrg = (org) => keep(VAULT_KEY, org ? { id: org.id, name: org.name } : null)

/** Whether the ledger open here is an encrypted org's. */
export const syncing = () => cloud.getMode() === 'cloud' && !!vaultOrg() && vaultOrg().id === cloud.getOrgId()

/**
 * Whether this browser has signed in from local mode (to move its ledger into a
 * new encrypted org). Local mode asks the server who is signed in only then — a
 * device that never did has nothing to ask, and may have no server to ask.
 */
const SIGNED_KEY = 'okane.syncAccount'
export const mightBeSignedIn = () => kept(SIGNED_KEY) === 1
export const markSignedIn = (yes) => keep(SIGNED_KEY, yes ? 1 : null)

/** Whether this copy has ever been filled from its vault (or made it). */
export async function isJoined() {
  try {
    return (await getMeta(direct, 'joined')) === '1'
  } catch {
    return false // no `_sync_meta` yet: a file that has never synced
  }
}

// ------------------------------------------------------------------ the device

function deviceStore(mode, fn) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('okane-sync', 1)
    req.onupgradeneeded = () => req.result.createObjectStore('device')
    req.onerror = () => reject(req.error)
    req.onsuccess = () => {
      const tx = req.result.transaction('device', mode)
      const out = fn(tx.objectStore('device'))
      tx.oncomplete = () => resolve(out.result)
      tx.onerror = () => reject(tx.error)
    }
  })
}

/** This browser's device: made once, kept in IndexedDB. */
async function thisDevice() {
  const kept = await deviceStore('readonly', (s) => s.get('this'))
  if (kept) return kept
  const { browser, os } = describe()
  const made = { ...(await newDevice()), name: [browser, os].filter(Boolean).join(' · ') || 'OKANE' }
  await deviceStore('readwrite', (s) => s.put(made, 'this'))
  return made
}
function describe() {
  const ua = navigator.userAgent
  const browser = /Firefox\//.test(ua) ? 'Firefox' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : null
  const os = /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : null
  return { browser, os }
}

// ------------------------------------------------------------------ the client

let vault = null
// Resolves once this device's client exists — a code opened at boot can arrive before.
let made
const vaultMade = new Promise((resolve) => (made = resolve))
// Another device's changes landed: read the screen's data again.
let onRemote = () => hydrate().catch((err) => console.error(err))
let reloadTimer = null
// The client's start (register, keys, the socket): unlocking waits for it, or
// the two would fill the same file at once.
let starting = null

/** A snapshot replaces the file: bring it to this code's schema, capture off meanwhile. */
async function importSnapshot(bytes) {
  await importDatabase(bytes)
  await uninstall(direct)
  await migrateNow()
}

/** A client for `org`, on `db` (the facade's `direct`, or a driver being set up). */
function client(org, device, { db = direct, exportDb = exportDatabase, live = true } = {}) {
  return createVault({
    db,
    orgId: org.id,
    device,
    local: LOCAL,
    exportDb,
    importDb: importSnapshot,
    schema: MIGRATIONS.length,
    base: cloud.apiBase(),
    exclusive: hold,
    onState: live ? onState : () => {},
    onChange: () => {
      clearTimeout(reloadTimer)
      reloadTimer = setTimeout(() => onRemote(), 120)
    },
    onPresence: (event) => {
      state.sync.editing = event.state === 'editing' ? { user: event.user, at: Date.now() } : null
      // A device that has just come along may be one waiting for the key.
      if (event.state !== 'editing') refreshWaiting()
    },
    onWaiting: () => refreshWaiting(),
  })
}

function onState(status, detail) {
  state.sync.status = status
  // Deleted, decrypted, or we were taken out of it: this copy is not the org's any
  // more. Boot opens what the org now is (or another), and this file goes.
  if (status === 'gone' && !state.cloud.encrypting) {
    const org = vaultOrg()
    if (org) {
      dropLater(vaultFile(org.id))
      dropLater(`files:${org.id}`)
    }
    setVaultOrg(null)
    location.reload()
    return
  }
  state.sync.error = status === 'error' ? (detail?.message ?? String(detail)) : null
  // Up to date means the snapshot is in: the ledger can be shown.
  if (status === 'synced') {
    state.sync.gate = false
    state.sync.code = null
    countConflicts()
    // Back in touch: attached files waiting to go up, go.
    import('./attachments').then((m) => m.flushOutbox()).catch(() => {})
  }
}

async function countConflicts() {
  try {
    state.sync.conflicts = (await vault.conflicts(1000)).length
  } catch {
    state.sync.conflicts = 0
  }
}

/** Devices of members still waiting for the key, with the code each shows. */
export async function refreshWaiting() {
  if (!vault || state.sync.status === 'locked') return (state.sync.waiting = [])
  try {
    state.sync.waiting = (await vault.waiting()).filter((d) => !d.mine)
  } catch {
    state.sync.waiting = []
  }
  return state.sync.waiting
}

const pushSoon = () => {
  if (!vault) return
  vault.pending().then((n) => n && vault.changed(), () => {})
}

/**
 * Starts syncing the encrypted org open here, once the app is up. `reload`
 * re-reads the screen when another device's changes land. Works offline: the
 * ledger is on this device, and pushes wait.
 */
export async function startSync({ reload } = {}) {
  const org = vaultOrg()
  if (!org) return
  if (reload) onRemote = reload
  const device = await thisDevice()
  vault ??= client(org, device)
  made(vault)
  Object.assign(state.sync, { on: true, org, status: 'connecting' })
  afterWrite.add(pushSoon)
  // Who is signed in, for Settings — the ledger itself does not wait for it.
  cloud.me().then(
    (account) => {
      state.cloud.account = account
      if (!account) state.sync.status = 'signed-out'
      else state.cloud.org = { ...org, ...(account.orgs ?? []).find((o) => o.id === org.id) }
    },
    () => {},
  )
  try {
    starting = vault.start()
    const result = await starting
    state.sync.role = vault.role
    if (result === 'locked') state.sync.code = await vault.code()
    else refreshWaiting()
  } catch (err) {
    const status = err?.status === 401 ? 'signed-out' : err?.code === 'offline' ? 'offline' : 'error'
    Object.assign(state.sync, { status, error: status === 'error' ? (err?.message ?? String(err)) : null })
  }
}

/**
 * An owner's device opening the org's key itself, with the passphrase or the
 * recovery key, rather than waiting to be approved.
 */
export async function unlock({ passphrase, recovery }) {
  await (vault ?? (await vaultMade))
  await starting?.catch(() => {})
  await vault.unlock({ passphrase, recovery })
  state.sync.code = null
  await vault.syncNow()
}

// ------------------------------------------------------------------ making one

/**
 * A ledger of this device into a new encrypted org: the org is made — under
 * the ledger's id (`id`), with its keyring (`keyring`, keeping its passphrase
 * and recovery key) when it has one — and this ledger set up as its first
 * sealed snapshot. Returns `{ org, recovery }`; `recovery` only for a new keyring.
 */
export async function turnOn({ name, passphrase, id, keyring }) {
  const org = await cloud.createOrg(name, 'vault', { id })
  try {
    const recovery = await client(org, await thisDevice(), { live: false }).setUp({ passphrase, keyring })
    return { org, recovery }
  } catch (err) {
    await cloud.deleteOrg(org.id).catch(() => {})
    throw err
  }
}

/**
 * A plain org encrypted in place, from an owner's device in cloud mode: its
 * ledger copied from the server into the org's file here, set up as the first
 * sealed snapshot, and the org turned into a vault by the server — which then
 * deletes the readable ledger and tells every device on it that it is gone.
 * The same org, the same people. Returns the recovery key; the reload after it
 * opens the org as the encrypted one it now is.
 */
export async function encryptInPlace({ org, passphrase, fileKeys = {} }) {
  const local = await openLocalDriver({ file: vaultFile(org.id) })
  try {
    await copyLedger({ from: activeDriver(), to: local })
    // Its files, sealed here already (stores/attachments.js): their keys go into the first snapshot.
    for (const [id, key] of Object.entries(fileKeys)) await local.run('UPDATE attachments SET file_key = ? WHERE id = ?', [key, id])
    const db = { all: (sql, p) => local.all(sql, p), run: (sql, p) => local.run(sql, p), exec: (sql) => local.exec(sql) }
    const recovery = await client(org, await thisDevice(), { db, exportDb: () => local.export(), live: false }).setUp({
      passphrase,
      encrypting: true,
    })
    setVaultOrg(org)
    return recovery
  } finally {
    local.close()
  }
}

/**
 * The second half of `turnOn`, at boot: the local ledger (already set up as
 * the org's first snapshot) is copied into the org's file and destroyed here —
 * two live copies would drift apart. Then this device opens the org.
 */
export async function finishMoveToVault(move, { eraseLocal }) {
  const local = await openLocalDriver()
  await local.copyTo(vaultFile(move.org.id))
  await eraseLocal(local)
  setVaultOrg(move.org)
  cloud.setOrgId(move.org.id)
  cloud.setMode('cloud')
}

/** The open org's keyring, still wrapped — for an owner's device keeping it encrypted here. */
export const vaultKeyring = async () => (vault ?? (await vaultMade)).keyring()

// ------------------------------------------------------------------ approving

export async function approve(deviceId) {
  await vault.approve(deviceId)
  await refreshWaiting()
}

export const conflicts = (limit = 50) => vault?.conflicts(limit) ?? Promise.resolve([])
export const syncNow = () => vault?.syncNow()

// ------------------------------------------------------------------ QR codes
//
// Both ways round (the phone scans):
//   ?login=…&org=<org>         cloud mode's device link, with the org: opening it
//                              signs the new device in and opens that org —
//                              an encrypted one then waits for its key, as ever
//   ?syncdevice=<id>.<print>   shown by a device waiting for the key: a device that
//                              syncs scans it and approves it. `print` is a hash of
//                              the waiting device's public key, so the approver
//                              checks the key the server hands over is that one —
//                              the six digits, compared by the camera.

const hex = (bytes) => [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
async function fingerprint(publicKey) {
  const raw = Uint8Array.from(atob(publicKey), (c) => c.charCodeAt(0))
  return hex(new Uint8Array(await crypto.subtle.digest('SHA-256', raw)).subarray(0, 16))
}

/** What a scanned or opened link asks, or null. Only this app's own links. */
export function readSyncCode(text, origin = location.origin) {
  let url
  try {
    url = new URL(String(text ?? '').trim())
  } catch {
    return null
  }
  if (url.origin !== origin) return null
  // `sync=` is what the first codes said.
  const org = url.searchParams.get('org') ?? url.searchParams.get('sync')
  const token = url.searchParams.get('login')
  if (org && token) return { kind: 'join', org, token, url: url.href }
  const device = url.searchParams.get('syncdevice')?.match(/^([A-Za-z0-9_-]{8,64})\.([0-9a-f]{32})$/)
  if (device) return { kind: 'device', id: device[1], print: device[2], url: url.href }
  return null
}

/** This waiting device's link, for a device that syncs to scan. */
export async function deviceLink() {
  const device = await thisDevice()
  return `${location.origin}/?syncdevice=${device.id}.${await fingerprint(device.publicKey)}`
}

/**
 * The device a scanned code names, checked: it must be waiting in this ledger,
 * and its key must be the one the code was made from.
 */
export async function scannedDevice({ id, print }) {
  const d = (await (vault ?? (await vaultMade)).devices()).find((x) => x.id === id)
  if (!d) throw Object.assign(new Error('That device is not waiting in this profile.'), { code: 'sync-no-device' })
  if ((await fingerprint(d.publicKey)) !== print) {
    throw Object.assign(new Error('That device’s key is not the one its code shows.'), { code: 'sync-key-mismatch' })
  }
  return d
}
